/**
 * O access DESTA aba — só em memória (faixa 25 §1, I1). Ver `acesso.md`.
 *
 * Nunca vai a localStorage, sessionStorage, cookie legível ou log. F5 = memória vazia: quem entra
 * renova pelo cookie HttpOnly da API (`PortaoDeSessao`, `login/page.tsx`).
 */
export type OrigemDoAcesso = 'login' | 'renovacao' | 'troca-senha'

/**
 * Esquema do `Authorization` (faixa 33 §5): o do `token_type` da ÚLTIMA emissão (B7). `DPoP` = sessão presa à
 * chave do navegador (toda request leva prova); `Bearer` = como antes da faixa 33 (e o rollback do back).
 */
export type EsquemaDoAcesso = 'DPoP' | 'Bearer'

export interface AcessoNovo {
  token: string
  /** SEGUNDOS até o `exp`, como o back manda (`expires_in`, ≤ 600). */
  expiresIn: number
  origem: OrigemDoAcesso
  /** `true` = chegou pelo canal entre abas; esta aba não chamou a API. */
  deOutraAba: boolean
  /** Obrigatório: um caminho que esquecesse o `token_type` mandaria `Bearer` com token preso (401 em laço). */
  esquema: EsquemaDoAcesso
}

/** A guarda de I7 (spec §10): `'mudou'` = já renovou, repete; `'sumiu'` = ESTA aba saiu. */
export type ComparacaoDoAcesso = 'igual' | 'mudou' | 'sumiu'

type Ouvinte = (novo: AcessoNovo) => void

let atual: string | null = null
let esquemaAtual: EsquemaDoAcesso = 'Bearer'
const ouvintes = new Set<Ouvinte>()

export function lerAcesso(): string | null {
  return atual
}

export function lerEsquema(): EsquemaDoAcesso {
  return esquemaAtual
}

/**
 * `token_type` da emissão (login, refresh, troca de senha, canal) → esquema. Sem caixa: o back manda `'DPoP'` e
 * `'bearer'`. Ausente ou desconhecido → `Bearer` — erra pro lado que se recupera sozinho (401 → renova).
 */
export function esquemaDe(tokenType: string | undefined): EsquemaDoAcesso {
  return tokenType?.toLowerCase() === 'dpop' ? 'DPoP' : 'Bearer'
}

/** Troca o access e avisa quem ouve (agendador, socket) — DEPOIS de trocar: o ouvinte já lê o novo. */
export function definirAcesso(novo: AcessoNovo): void {
  atual = novo.token
  esquemaAtual = novo.esquema
  for (const ouvir of [...ouvintes]) ouvir(novo)
}

/** Só a saída limpa (`saida.ts`), e ela navega: não avisa ninguém. */
export function limparAcesso(): void {
  atual = null
  esquemaAtual = 'Bearer'
}

export function ouvirAcesso(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte)
  return () => {
    ouvintes.delete(ouvinte)
  }
}

export function compararAcesso(usado: string | null): ComparacaoDoAcesso {
  if (atual === null) return 'sumiu'
  return atual === usado ? 'igual' : 'mudou'
}

/** Só para teste: o módulo sobrevive entre casos do mesmo arquivo. */
export function __resetAcesso(): void {
  atual = null
  esquemaAtual = 'Bearer'
  ouvintes.clear()
}
